import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, Action } from '@ngrx/store';
import { noop, Observable, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {
  AssignmentCustomerResponse,
  AssignmentDateListItemResponse,
  AssignmentPspResponse,
  ContractResponse,
  CustomerAssignmentListItemResponse,
  CustomerDepartmentQualificationsListItemResponse,
  GeneralApiResponse,
  PaginatedApiResponse,
  PspAssignmentListItemResponse,
} from '../../../api';
import { BaseRolesService } from '../../core/services/base-roles.service';
import { catchError, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { BaseToastAlertClasses } from '../../shared/enums/base-toast-alert-classes.enum';
import { BaseTokenInterface } from '../../shared/interfaces/base-token-service.interface';
import { BaseAssignmentDateStatusEnum } from '../../modules/assignments/_enums/base-assignment-date-status.enum';
import { BaseAssignmentsService } from '../../modules/assignments/base-assignments-service';
import { baseAssignmentsActions, baseOffersActions, baseSharedActions } from '../actions';
import { BaseCoreState } from '../reducers';
import { baseAssignmentsSelectors, baseAuthSelectors } from '../selectors';
import { BaseSignContractOptions } from '../../modules/assignments/_enums/base-sign-contract-options.enum';
import { ExportPspContractsRequestInterface } from '../../modules/assignments/_interfaces/export-psp-contracts-request.interface';
import { BaseModalService } from '../../shared/services/base-modal.service';
import { BaseFileDownloadService } from '../../shared/services/base-file-download.service';
import { BaseMimeType, BaseFileExtension } from '../../shared/enums/base-available-file-type.enum';
import { ExportPspAssignmentInformationRequestInterface } from '../../modules/assignments/_interfaces/export-psp-assignment-information-request.interface';
import { ExportCustomerAssignmentInformationRequestInterface } from '../../modules/assignments/_interfaces/export-customer-assignment-information-request.interface';
import { BaseAssignmentsListPayloadInterface } from '../../modules/assignments/_interfaces/base-assignments-list-payload.interface';
import { BaseDateService } from '../../shared/services/base-date.service';
import { BaseAssignmentsHelpersService } from '../../modules/assignments/base-assignments-helpers.service';
import { BaseSigningStatus } from '../../modules/assignments/_enums/base-signing-status.enum';

@Injectable()
export class BaseAssignmentsEffects {
  loadAssignmentList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseAssignmentsActions.loadAssignmentList),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        let source$: Observable<
          PaginatedApiResponse & {
            data?: Array<CustomerAssignmentListItemResponse> | Array<PspAssignmentListItemResponse>;
          }
        >;

        source$ = this.roleService.isPsp()
          ? this.assignmentsService.getPspAllAssignments({
              ...action.payload,
              psp: user.organizationId,
            })
          : this.assignmentsService.getAllCustomerAssignments({
              ...action.payload,
              customer: user.organizationId,
            });

        return source$.pipe(
          map((response) => {
            return baseAssignmentsActions.setAssignmentList({ payload: response });
          }),
          catchError(() => of(baseAssignmentsActions.errorAssignmentList()))
        );
      })
    );
  });

  loadAssignmentDates$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseAssignmentsActions.loadAssignmentDates),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        let source$;
        if (this.roleService.isPsp()) {
          source$ = this.assignmentsService.getPspAssignmentDates(
            user.organizationId,
            action.payload.assignment,
            action.payload.status
          );
        } else {
          source$ = this.assignmentsService.getCustomerAssignmentDates(
            user.organizationId,
            action.payload.assignment,
            action.payload.status
          );
        }
        return source$.pipe(
          mergeMap((response) => [
            baseAssignmentsActions.setAssignmentDates({ payload: response } as any),
            baseAssignmentsActions.successAssignmentDates(),
          ]),
          catchError(() => of(baseAssignmentsActions.errorAssignmentDates()))
        );
      })
    );
  });

  loadAssignment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseAssignmentsActions.loadAssignment),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        const source$: Observable<GeneralApiResponse & { data?: AssignmentPspResponse | AssignmentCustomerResponse }> =
          this.roleService.isPsp()
            ? this.assignmentsService.getPspAssignment(user.organizationId, action.payload.assignment)
            : this.assignmentsService.getCustomerAssignment(user.organizationId, action.payload.assignment);
        return source$.pipe(
          tap((response) => {
            const validContract = response.data?.contracts?.find((contract) => contract.valid) as ContractResponse;

            if (action.payload.showDigitalSignStatus) {
              const signingStatus = this.roleService.isPsp()
                ? validContract.signatures?.statusPsp
                : validContract.signatures?.statusCustomer;

              switch (signingStatus) {
                case BaseSigningStatus.signed:
                  const alert = `
                    <h3 class="headline-20">${this.translateService.instant(
                      'digitalSigningProcess.signCompletedTitle'
                    )}</h3>
                    ${this.translateService.instant('digitalSigningProcess.signCompletedDescription')}
                  `;

                  this.store.dispatch(
                    baseSharedActions.addSystemAlert({
                      payload: {
                        class: BaseToastAlertClasses.success,
                        body: alert,
                        closeManually: true,
                      },
                    })
                  );
                  break;
                case BaseSigningStatus.in_queue:
                  this.modalService
                    .open(
                      {
                        headText: 'operations.signatureIsBeingProcessed',
                        bodyText: 'operations.signaturePendingMessage',
                        okText: 'action.close',
                        hideCloseIcon: false,
                        cancelButtonDisplayed: false,
                        cancelButtonOnly: true,
                      },
                      {
                        size: 'lg',
                      }
                    )
                    .result.then(noop, noop);
                  break;
                case BaseSigningStatus.error:
                  this.store.dispatch(
                    baseSharedActions.addSystemAlert({
                      payload: {
                        class: BaseToastAlertClasses.error,
                        body: 'operations.signErrorHeader',
                      },
                    })
                  );
                  break;
              }
            }
          }),
          mergeMap((response) => [
            baseAssignmentsActions.setAssignment({ payload: response.data }),
            baseAssignmentsActions.successLoadAssignment(),
          ]),
          catchError(() => of(baseAssignmentsActions.errorLoadAssignment()))
        );
      })
    );
  });

  loadCustomerAllAssignments$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseAssignmentsActions.loadCustomerAllAssignments),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.assignmentsService
          .getAllCustomerAssignments({
            ...action.payload,
            customer: user.organizationId,
          })
          .pipe(
            mergeMap((response) => [
              baseAssignmentsActions.setCustomerAllAssignments({ payload: response }),
              baseAssignmentsActions.successCustomerAllAssignment(),
            ]),
            catchError(() => of(baseAssignmentsActions.errorCustomerAllAssignment()))
          );
      })
    );
  });

  downloadCustomerContracts$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseAssignmentsActions.downloadCustomerContracts),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        const payload = { ...action.payload.contractsInfo, customer: user.organizationId };
        const source$ = this.assignmentsService.exportCustomerContracts(payload);

        return this.fileDownloadService.downloadFile(source$, { documentType: BaseFileExtension.zip }).pipe(
          tap(() => {
            if (action.payload.noContractProfiles.length > 0) {
              this.assignmentsHelpersService.showNotDownloadedContractsAlert(action.payload.noContractProfiles);
            }
          }),
          map(() => baseAssignmentsActions.successDownloadCustomerContracts()),
          catchError(() => of(baseAssignmentsActions.errorDownloadCustomerContracts()))
        );
      })
    );
  });

  downloadPspContracts$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseAssignmentsActions.downloadPspContracts),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        const payload = {
          ...action.payload.contractsInfo,
          psp: user.organizationId,
        } as ExportPspContractsRequestInterface;
        const source$ = this.assignmentsService.exportPspContracts(payload);

        return this.fileDownloadService.downloadFile(source$, { documentType: BaseFileExtension.zip }).pipe(
          tap(() => {
            if (action.payload.noContractProfiles.length > 0) {
              this.assignmentsHelpersService.showNotDownloadedContractsAlert(action.payload.noContractProfiles);
            }
          }),
          map(() => baseAssignmentsActions.successDownloadPspContracts()),
          catchError(() => of(baseAssignmentsActions.errorDownloadPspContracts()))
        );
      })
    );
  });

  downloadPspAssignmentInfo$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseAssignmentsActions.downloadPspAssignmentInfo),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        const payload = {
          ...action.payload,
          psp: user.organizationId,
        } as ExportPspAssignmentInformationRequestInterface;
        const source$ = this.assignmentsService.exportPspAssignmentInfo(payload);

        return this.fileDownloadService.downloadFile(source$, { documentType: BaseFileExtension.csv }).pipe(
          map(() => baseAssignmentsActions.successDownloadPspAssignment()),
          catchError(() => of(baseAssignmentsActions.errorDownloadPspAssignment()))
        );
      })
    );
  });

  downloadCustomerAssignmentInfo$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseAssignmentsActions.downloadCustomerAssignmentInfo),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        const payload = {
          ...action.payload,
          customer: user.organizationId,
        } as ExportCustomerAssignmentInformationRequestInterface;
        const source$ = this.assignmentsService.exportCustomerAssignmentInfo(payload);

        return this.fileDownloadService.downloadFile(source$, { documentType: BaseFileExtension.csv }).pipe(
          map(() => baseAssignmentsActions.successDownloadCustomerAssignmentInfo()),
          catchError(() => of(baseAssignmentsActions.errorDownloadCustomerAssignmentInfo()))
        );
      })
    );
  });

  confirmAssignment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseAssignmentsActions.confirmAssignment),
      withLatestFrom(this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data))),
      switchMap(([action, user]) =>
        this.assignmentsService
          .confirmAssignment(action.payload.assignment, action.payload.assignmentDate, user?.organizationId || -1)
          .pipe(
            withLatestFrom(
              this.store.select(baseAssignmentsSelectors.getAssignmentDates).pipe(map((dates) => dates.data.data))
            ),
            mergeMap(([_response, dates]) => [
              baseAssignmentsActions.updateAssignmentDate({
                payload: {
                  ...dates.find((date: AssignmentDateListItemResponse) => date.id === action.payload.assignmentDate),
                  status: BaseAssignmentDateStatusEnum.ACCEPTED,
                },
              }),
              baseAssignmentsActions.confirmAssignmentSuccess(),
            ]),
            catchError(() => {
              return of(baseAssignmentsActions.confirmAssignmentError());
            })
          )
      )
    )
  );

  createUpdateAssignmentTimes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseAssignmentsActions.createUpdateAssignmentTimes),
      withLatestFrom(this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data))),
      switchMap(([action, user]) => {
        return this.assignmentsService
          .createUpdateAssignmentTimes({
            psp: (user as BaseTokenInterface).organizationId,
            assignment: action.payload.assignment,
            assignmentDate: action.payload.assignmentDate,
            body: action.payload.body,
          })
          .pipe(
            mergeMap(() => [
              baseAssignmentsActions.createUpdateAssignmentTimesSuccess(),
              baseAssignmentsActions.loadAssignmentDates({ payload: { assignment: action.payload.assignment } }),
            ]),
            catchError(() => of(baseAssignmentsActions.createUpdateAssignmentTimesError()))
          );
      })
    )
  );

  loadAssignmentDateTimestampList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseAssignmentsActions.loadAssignmentDateTimestampList),
      switchMap((action) =>
        this.assignmentsService.getCustomerAssignmentDateTimestamps(action.payload).pipe(
          mergeMap((response) => [
            baseAssignmentsActions.setAssignmentDateTimestampList({ payload: response.data || {} }),
            baseAssignmentsActions.successAssignmentDateTimestampList(),
          ]),
          catchError(() => of(baseAssignmentsActions.errorAssignmentDateTimestampList()))
        )
      )
    )
  );

  updateAssignmentDateTimestamps$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseAssignmentsActions.updateAssignmentDateTimestamps),
      switchMap((action) =>
        this.assignmentsService.updateCustomerAssignmentDateTimestamps(action.payload).pipe(
          mergeMap(() => [
            baseAssignmentsActions.successUpdateAssignmentDateTimestamps(),
            baseAssignmentsActions.loadAssignmentDateTimestampList({
              payload: {
                customer: action.payload.customer,
                assignmentDate: action.payload.assignmentDate,
              },
            }),
          ]),
          catchError(() => of(baseAssignmentsActions.errorUpdateAssignmentDateTimestamps()))
        )
      )
    )
  );

  signContractCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseAssignmentsActions.signContract),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      // @ts-ignore
      switchMap(([action, user]) => {
        let method;
        if (this.roleService.isPsp()) {
          method =
            (action.payload.signingType as BaseSignContractOptions) === BaseSignContractOptions.signExternal
              ? this.assignmentsService.pspSignContractExternal({
                  psp: user.organizationId,
                  contract: action.payload.contract,
                })
              : action.payload.signingType === BaseSignContractOptions.concretization
              ? this.assignmentsService.pspSignContractConcretization({
                  psp: user.organizationId,
                  contract: action.payload.contract,
                })
              : this.assignmentsService.pspSignContractDigitally({
                  psp: user.organizationId,
                  contract: action.payload.contract,
                });
        } else {
          method =
            (action.payload.signingType as BaseSignContractOptions) === BaseSignContractOptions.signExternal
              ? this.assignmentsService.customerSignContractExternal({
                  customer: user.organizationId,
                  contract: action.payload.contract,
                })
              : this.assignmentsService.customerSignContractDigitally({
                  customer: user.organizationId,
                  contract: action.payload.contract,
                });
        }

        const alertMessage =
          action.payload.signingType === BaseSignContractOptions.concretization
            ? 'operations.concretionSuccessMessage'
            : 'operations.signSuccess';

        return method?.pipe(
          withLatestFrom(this.translateService.get(alertMessage)),
          mergeMap(([_, message]) => {
            const isDigitalSignature = action.payload.signingType === BaseSignContractOptions.signDigital;
            const reloadAssignmentInfo = action.payload.reloadAssignmentInfo;
            let actions: any[] = [baseAssignmentsActions.signContractSuccess()];
            if (reloadAssignmentInfo) {
              actions.push(
                baseAssignmentsActions.loadAssignmentDates({ payload: { assignment: action.payload.assignment } })
              );
            }
            if (isDigitalSignature) {
              actions.push(
                baseAssignmentsActions.loadAssignment({
                  payload: {
                    assignment: action.payload.assignment,
                    showDigitalSignStatus: isDigitalSignature,
                  },
                })
              );
            } else {
              // This success message shouldn't be displayed for digital signature process
              // Since digital signature is async process
              // Displaying of message in case of digital signature is handled in effect for loadAssignment action based on showDigitalSignStatus payload field value
              actions.push(
                baseSharedActions.addSystemAlert({
                  payload: {
                    class: BaseToastAlertClasses.success,
                    body: message,
                  },
                })
              );
            }

            return actions;
          }),
          catchError(() => of(baseAssignmentsActions.signContractError()))
        );
      })
    )
  );

  assignmentCancellation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseAssignmentsActions.assignmentCancellation),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        const method = this.roleService.isPsp()
          ? this.assignmentsService.cancelAssignmentPsp(
              user?.organizationId,
              action.payload.assignment,
              action.payload.body
            )
          : this.assignmentsService.cancelAssignmentCustomer(
              user?.organizationId,
              action.payload.assignment,
              action.payload.body
            );

        return method.pipe(
          mergeMap((_) => {
            const queryParams = this.activatedRoute.snapshot.queryParams;

            return [
              baseAssignmentsActions.loadAssignmentList({
                payload: queryParams as BaseAssignmentsListPayloadInterface,
              }),
              baseAssignmentsActions.loadAssignment({ payload: { assignment: action.payload.assignment } }),
              baseAssignmentsActions.assignmentCancellationSuccess(),
            ];
          }),
          catchError(() => of(baseAssignmentsActions.assignmentCancellationError()))
        );
      })
    )
  );

  assignmentCustomerCancellation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseAssignmentsActions.assignmentCustomerCancellation),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.assignmentsService
          .cancelAssignmentCustomer(user?.organizationId, action.payload.assignment, action.payload.body)
          .pipe(
            mergeMap((_) => {
              return [
                baseAssignmentsActions.loadCustomerAllAssignments({
                  payload: this.activatedRoute.snapshot.queryParams as BaseAssignmentsListPayloadInterface,
                }),
                baseAssignmentsActions.loadAssignment({ payload: { assignment: action.payload.assignment } }),
                baseAssignmentsActions.assignmentCancellationSuccess(),
              ];
            }),
            catchError(() => of(baseAssignmentsActions.assignmentCancellationError()))
          );
      })
    )
  );

  loadCustomerendedAssignments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseAssignmentsActions.getCandidateEndedAssignments),
      withLatestFrom(this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data))),
      switchMap(([action, user]) => {
        return this.assignmentsService.fetchCandidateEndedAssignments(user?.organizationId || -1, action.payload).pipe(
          mergeMap((response) => [
            baseAssignmentsActions.loadCandidateEndedAssignments({ payload: response }),
            baseAssignmentsActions.loadCandidateEndedAssignmentsSuccess(),
          ]),
          catchError(() => of(baseAssignmentsActions.loadCandidateEndedAssignmentsError()))
        );
      })
    )
  );

  loadCustomerReview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseAssignmentsActions.getCustomerReviews),
      withLatestFrom(this.store.select(baseAuthSelectors.getUserInfo).pipe(map((response) => response.data))),
      switchMap(([action, user]) => {
        return this.assignmentsService.fetchCandidateReviews(user?.organizationId || -1, action.payload).pipe(
          mergeMap((response) => [
            baseAssignmentsActions.loadCustomerReviews({ payload: response }),
            baseAssignmentsActions.loadCustomerReviewsSuccessfully(),
          ]),
          catchError(() => of(baseAssignmentsActions.loadCustomerReviewsError()))
        );
      })
    )
  );

  createCandidateReview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseAssignmentsActions.createCandidateRating),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data)),
        this.store.select(baseAssignmentsSelectors.getAssignment).pipe(map((assignment) => assignment.data))
      ),
      switchMap(([action, user, assignment]) => {
        return this.assignmentsService
          .createCandidateRating(
            user?.organizationId || -1,
            assignment?.profile.id || action.payload.assignment?.profile.id || action.payload.profileId,
            action.payload.body
          )
          .pipe(
            mergeMap(() => [
              baseAssignmentsActions.createCandidateRatingSuccess(),
              baseAssignmentsActions.getCustomerReviews({
                payload: assignment?.profile.id || action.payload.assignment?.profile.id || action.payload.profileId,
              }),
            ]),
            catchError(() => of(baseAssignmentsActions.createCandidateRatingError()))
          );
      })
    )
  );

  updateCandidateReview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseAssignmentsActions.updateCandidateRating),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data)),
        this.store.select(baseAssignmentsSelectors.getAssignment).pipe(map((assignment) => assignment.data))
      ),
      switchMap(([action, user, assignment]) => {
        return this.assignmentsService
          .updateCandidateRating(
            user?.organizationId || -1,
            assignment?.profile.id || action.payload.assignment?.profile.id || action.payload.profileId,
            action.payload.rating,
            action.payload.body
          )
          .pipe(
            mergeMap((_) => [
              baseAssignmentsActions.updateCandidateRatingSuccess(),
              baseAssignmentsActions.getCustomerReviews({
                payload: assignment?.profile.id || action.payload.assignment?.profile.id || action.payload.profileId,
              }),
            ]),
            catchError(() => of(baseAssignmentsActions.updateCandidateRatingError()))
          );
      })
    )
  );

  loadAssignmentCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseAssignmentsActions.loadAssignmentCustomer),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        const source$ = this.roleService.isPsp()
          ? this.assignmentsService.getPspsCustomer(user.organizationId, action.payload.customer)
          : this.assignmentsService.getCustomersCustomer(action.payload.customer);

        return source$.pipe(
          map((response) => baseAssignmentsActions.setAssignmentCustomer({ payload: response })),
          catchError(() => of(baseAssignmentsActions.assignmentCancellationError()))
        );
      })
    )
  );

  updateCustomerProfilePersonalId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseAssignmentsActions.updateCustomerProfilePersonalId),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.assignmentsService
          .updateCustomerProfileInternalPersonalId({
            customer: user.organizationId,
            profile: action.payload.profile,
            body: action.payload.body,
          })
          .pipe(
            mergeMap((_) => [
              baseAssignmentsActions.successUpdateCustomerProfilePersonalId(),
              baseAssignmentsActions.loadAssignment({ payload: { assignment: action.payload.assignment } }),
            ]),
            catchError(() => of(baseAssignmentsActions.errorUpdateCustomerProfilePersonalId()))
          );
      })
    )
  );

  downloadContract$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseAssignmentsActions.viewContract),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        const source$: Observable<any> = this.roleService.isPsp()
          ? this.assignmentsService.downloadPspContract(
              user.organizationId,
              action.payload.assignment,
              action.payload.contract
            )
          : this.assignmentsService.downloadCustomerContact(
              user.organizationId,
              action.payload.assignment,
              action.payload.contract
            );
        return source$.pipe(
          mergeMap((response) => [
            baseAssignmentsActions.setDocumentView({
              payload: {
                document: new Blob([response.body], { type: BaseMimeType.pdf }),
                fileName: response.fileName,
              },
            }),
            baseAssignmentsActions.successDownloadContract(),
          ]),
          catchError(() => of(baseAssignmentsActions.errorDownloadContract()))
        );
      })
    );
  });

  downloadDocumentFile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseAssignmentsActions.downloadCustomerCandidateDocument),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        const source$ = this.assignmentsService.downloadCustomerCandidateDocumentFile({
          assignment: action.payload.assignment,
          document: action.payload.document,
          customer: user.organizationId,
        });

        return source$.pipe(
          switchMap((response) =>
            this.fileDownloadService
              .downloadFile(of(response), {
                isPreview: action.payload.openInModal,
                documentType: action.payload.documentType,
              })
              .pipe(
                mergeMap((blob) => {
                  const actions: Action[] = [baseAssignmentsActions.successDownloadCustomerCandidateDocument()];

                  if (blob) {
                    actions.unshift(
                      baseAssignmentsActions.setDocumentView({
                        payload: {
                          document: blob,
                          fileName: response.fileName,
                        },
                      })
                    );
                  }

                  return actions;
                })
              )
          ),
          catchError(() => of(baseAssignmentsActions.errorDownloadCustomerCandidateDocument()))
        );
      })
    );
  });

  regeneratePspAssignmentContract$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseAssignmentsActions.regeneratePspAssignmentContract),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.assignmentsService
          .regeneratePspAssignmentContract({
            ...action.payload,
            psp: user.organizationId,
          })
          .pipe(
            mergeMap((_) => {
              const queryParams = this.activatedRoute.snapshot.queryParams;

              return [
                baseAssignmentsActions.successRegeneratePspAssignmentContract(),
                baseAssignmentsActions.loadAssignment({ payload: { assignment: action.payload.assignment } }),
                baseAssignmentsActions.loadAssignmentList({
                  payload: queryParams as BaseAssignmentsListPayloadInterface,
                }),
              ];
            }),
            catchError(() => of(baseAssignmentsActions.errorRegeneratePspAssignmentContract()))
          );
      })
    )
  );

  regenerateCustomerAssignmentContract$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseAssignmentsActions.regenerateCustomerAssignmentContract),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.assignmentsService
          .regenerateCustomerAssignmentContract({
            ...action.payload,
            customer: user.organizationId,
          })
          .pipe(
            mergeMap((_) => {
              return [
                baseAssignmentsActions.successRegenerateCustomerAssignmentContract(),
                baseAssignmentsActions.loadAssignment({ payload: { assignment: action.payload.assignment } }),
                baseAssignmentsActions.loadCustomerAllAssignments({
                  payload: this.activatedRoute.snapshot.queryParams as BaseAssignmentsListPayloadInterface,
                }),
              ];
            }),
            catchError(() => of(baseAssignmentsActions.errorRegenerateCustomerAssignmentContract()))
          );
      })
    )
  );

  regenerateAssignmentProfileIDCard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseAssignmentsActions.regenerateAssignmentProfileIDCard),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        const source$ = this.roleService.isPsp()
          ? this.assignmentsService.regeneratePSPAssignmentProfileIDCard({
              psp: user.organizationId,
              assignment: action.payload.assignment,
              profile: action.payload.profile,
            })
          : this.assignmentsService.regenerateCustomerAssignmentProfileIDCard({
              customer: user.organizationId,
              assignment: action.payload.assignment,
              profile: action.payload.profile,
            });

        return source$.pipe(
          mergeMap((_) => [baseAssignmentsActions.successRegenerateAssignmentProfileIDCard()]),
          catchError(() => of(baseAssignmentsActions.errorRegenerateAssignmentProfileIDCard()))
        );
      })
    )
  );

  downloadAssignmentProfileIDCard$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseAssignmentsActions.downloadAssignmentProfileIDCard),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        const source$ = this.roleService.isPsp()
          ? this.assignmentsService.getPSPAssignmentProfileIDCard({
              psp: user.organizationId,
              assignment: action.payload.assignment,
              profile: action.payload.profile,
            })
          : this.assignmentsService.getCustomerAssignmentProfileIDCard({
              customer: user.organizationId,
              assignment: action.payload.assignment,
              profile: action.payload.profile,
            });

        const shouldPreview = action.payload.openInModal && !action.payload.downloadOnly;

        return source$.pipe(
          switchMap((response) => {
            return this.fileDownloadService
              .downloadFile(of(response), {
                isPreview: shouldPreview,
                documentType: BaseFileExtension.pdf,
              })
              .pipe(
                mergeMap((blob) => {
                  const actions: Action[] = [baseAssignmentsActions.successDownloadAssignmentProfileIDCard()];

                  if (blob) {
                    actions.unshift(
                      baseAssignmentsActions.setAssignmentProfileIDCardView({
                        payload: {
                          document: blob,
                          fileName: response.fileName,
                        },
                      })
                    );
                  }

                  return actions;
                })
              );
          }),
          catchError(() => of(baseAssignmentsActions.errorDownloadAssignmentProfileIDCard()))
        );
      })
    );
  });

  approveInquiryOffer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseAssignmentsActions.approveInquiryOffer),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        const queryParams = this.activatedRoute.snapshot.queryParams;

        return this.assignmentsService
          .approveInquiryOffer({
            customer: user.organizationId,
            offer: action.payload.offer,
            body: action.payload.body,
          })
          .pipe(
            mergeMap((_) => [
              baseAssignmentsActions.successApproveInquiryOffer(),
              baseAssignmentsActions.loadWorkCouncilCommentList({
                payload: {
                  offer: action.payload.offer,
                },
              }),
              baseOffersActions.loadOffersList({ payload: { ...queryParams, customer: user.organizationId } }),
              baseOffersActions.loadOfferDetails({ payload: { offer: action.payload.offer } }),
            ]),
            catchError(() => of(baseAssignmentsActions.errorApproveInquiryOffer()))
          );
      })
    )
  );

  disapproveInquiryOffer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseAssignmentsActions.disapproveInquiryOffer),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        const queryParams = this.activatedRoute.snapshot.queryParams;

        return this.assignmentsService
          .disapproveInquiryOffer({
            customer: user.organizationId,
            offer: action.payload.offer,
            body: action.payload.body,
          })
          .pipe(
            mergeMap((_) => [
              baseAssignmentsActions.successDisapproveInquiryOffer(),
              baseAssignmentsActions.loadWorkCouncilCommentList({
                payload: {
                  offer: action.payload.offer,
                },
              }),
              baseOffersActions.loadOffersList({ payload: { ...queryParams, customer: user.organizationId } }),
              baseOffersActions.loadOfferDetails({ payload: { offer: action.payload.offer } }),
            ]),
            catchError(() => of(baseAssignmentsActions.errorDisapproveInquiryOffer()))
          );
      })
    )
  );

  noteInquiryOffer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseAssignmentsActions.noteInquiryOffer),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        const queryParams = this.activatedRoute.snapshot.queryParams;

        return this.assignmentsService
          .noteInquiryOffer({
            customer: user.organizationId,
            offer: action.payload.offer,
            body: action.payload.body,
          })
          .pipe(
            mergeMap((_) => [
              baseAssignmentsActions.successNoteInquiryOffer(),
              baseAssignmentsActions.loadWorkCouncilCommentList({
                payload: {
                  offer: action.payload.offer,
                },
              }),
              baseOffersActions.loadOffersList({ payload: { ...queryParams, customer: user.organizationId } }),
              baseOffersActions.loadOfferDetails({ payload: { offer: action.payload.offer } }),
            ]),
            catchError(() => of(baseAssignmentsActions.errorNoteInquiryOffer()))
          );
      })
    )
  );

  createCustomerOfferComment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseAssignmentsActions.createWorkCouncilComment),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.assignmentsService
          .createCustomerOfferComment({
            customer: user.organizationId,
            offer: action.payload.offer,
            body: action.payload.body,
          })
          .pipe(
            mergeMap((_) => [
              baseAssignmentsActions.successCreateWorkCouncilComment(),
              baseAssignmentsActions.loadWorkCouncilCommentList({
                payload: {
                  offer: action.payload.offer,
                },
              }),
            ]),
            catchError(() => of(baseAssignmentsActions.errorCreateWorkCouncilComment()))
          );
      })
    )
  );

  getCustomerOfferCommentList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseAssignmentsActions.loadWorkCouncilCommentList),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.assignmentsService
          .getCustomerOfferCommentList({
            customer: user.organizationId,
            offer: action.payload.offer,
          })
          .pipe(
            mergeMap((response) => [
              baseAssignmentsActions.setWorkCouncilCommentList({
                payload: response.data,
              }),
              baseAssignmentsActions.successWorkCouncilCommentList(),
            ]),
            catchError(() => of(baseAssignmentsActions.errorWorkCouncilCommentList()))
          );
      })
    )
  );

  loadUpcomingEPAssignments = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseAssignmentsActions.loadUpcomingEPAssignments),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        let source$: Observable<
          PaginatedApiResponse & {
            data?: Array<CustomerAssignmentListItemResponse> | Array<PspAssignmentListItemResponse>;
          }
        >;

        const upcomingEPAssignmentsPayload = {
          status: 'planned,active',
          order: 'ep,ASC',
        };

        source$ = this.roleService.isPsp()
          ? this.assignmentsService.getPspAllAssignments({
              psp: user.organizationId,
              ...upcomingEPAssignmentsPayload,
            })
          : this.assignmentsService.getAllCustomerAssignments({
              customer: user.organizationId,
              ...upcomingEPAssignmentsPayload,
            });

        return source$.pipe(
          mergeMap((response: any) => {
            const assignments: CustomerAssignmentListItemResponse[] | PspAssignmentListItemResponse[] = response.data
              .filter(
                (assignment: CustomerAssignmentListItemResponse | PspAssignmentListItemResponse) =>
                  !!assignment.ep && BaseDateService.isEqualOrAfter(new Date(assignment.ep), new Date(), true)
              )
              .slice(0, 5);
            return [
              baseAssignmentsActions.setUpcomingEPAssignments({
                payload: {
                  assignments,
                  totalCount: response.data.length,
                },
              }),
              baseAssignmentsActions.successUpcomingEPAssignments(),
            ];
          }),
          catchError(() => of(baseAssignmentsActions.errorUpcomingEPAssignments()))
        );
      })
    );
  });

  loadUpcomingMLPAssignments = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseAssignmentsActions.loadUpcomingMLPAssignments),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        let source$: Observable<
          PaginatedApiResponse & {
            data?: Array<CustomerAssignmentListItemResponse> | Array<PspAssignmentListItemResponse>;
          }
        >;

        const upcomingEPAssignmentsPayload = {
          status: 'planned,active',
          order: 'mlp,ASC',
        };

        source$ = this.roleService.isPsp()
          ? this.assignmentsService.getPspAllAssignments({
              psp: user.organizationId,
              ...upcomingEPAssignmentsPayload,
            })
          : this.assignmentsService.getAllCustomerAssignments({
              customer: user.organizationId,
              ...upcomingEPAssignmentsPayload,
            });

        return source$.pipe(
          mergeMap((response: any) => {
            const assignments: CustomerAssignmentListItemResponse[] | PspAssignmentListItemResponse[] = response.data
              .filter(
                (assignment: CustomerAssignmentListItemResponse | PspAssignmentListItemResponse) =>
                  !!assignment.mlp && BaseDateService.isEqualOrAfter(new Date(assignment.mlp), new Date(), true)
              )
              .slice(0, 5);
            return [
              baseAssignmentsActions.setUpcomingMLPAssignments({
                payload: {
                  assignments,
                  totalCount: response.data.length,
                },
              }),
              baseAssignmentsActions.successUpcomingMLPAssignments(),
            ];
          }),
          catchError(() => of(baseAssignmentsActions.errorUpcomingMLPAssignments()))
        );
      })
    );
  });

  loadLocationDepartmentQualificationList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseAssignmentsActions.loadLocationDepartmentQualificationList),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.assignmentsService
          .getLocationDepartmentQualificationList({
            psp: user.organizationId,
            customer: action.payload.customer,
            location: action.payload.location,
            department: action.payload.department,
            category: action.payload.category,
            tag: action.payload.tag,
            search: action.payload.search,
          })
          .pipe(
            mergeMap((response) => [
              baseAssignmentsActions.setLocationDepartmentQualificationList({
                payload: response as CustomerDepartmentQualificationsListItemResponse[],
              }),
              baseAssignmentsActions.successLocationDepartmentQualificationList(),
            ]),
            catchError(() => of(baseAssignmentsActions.errorLocationDepartmentQualificationList()))
          );
      })
    )
  );

  loadPspCustomerLocationDepartment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseAssignmentsActions.loadPspCustomerLocationDepartment),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.assignmentsService
          .getPspCustomerLocationDepartment({
            psp: user.organizationId,
            customer: action.payload.customer,
            location: action.payload.location,
            department: action.payload.department,
          })
          .pipe(
            mergeMap((response) => [
              baseAssignmentsActions.setPspCustomerLocationDepartment({
                payload: response,
              }),
              baseAssignmentsActions.successPspCustomerLocationDepartment(),
            ]),
            catchError(() => of(baseAssignmentsActions.errorPspCustomerLocationDepartment()))
          );
      })
    )
  );

  loadPspAssignmentDateList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseAssignmentsActions.loadPspAssignmentDateList),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.assignmentsService
          .getAssignmentDateList({
            psp: user.organizationId,
            profile: action.payload.profile,
          })
          .pipe(
            mergeMap((response) => [
              baseAssignmentsActions.setPspAssignmentDateList({
                payload: response,
              }),
              baseAssignmentsActions.successPspAssignmentDateList(),
            ]),
            catchError(() => of(baseAssignmentsActions.errorPspAssignmentDateList()))
          );
      })
    )
  );

  createPspAssignment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseAssignmentsActions.createPspAssignment),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.assignmentsService
          .createPspAssignment({
            psp: user.organizationId,
            body: action.payload.body,
          })
          .pipe(
            mergeMap(() => [
              baseAssignmentsActions.loadAssignmentList({
                payload: this.activatedRoute.snapshot.queryParams as BaseAssignmentsListPayloadInterface,
              }),
              baseAssignmentsActions.successCreatePspAssignment(),
            ]),
            catchError(() => of(baseAssignmentsActions.errorCreatePspAssignment()))
          );
      })
    )
  );

  uploadAssignmentContract$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseAssignmentsActions.uploadAssignmentContract),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        const source$ = this.roleService.isPsp()
          ? this.assignmentsService.uploadPSPAssignmentContract({
              psp: user.organizationId,
              body: {
                file: action.payload.file,
              },
            })
          : this.assignmentsService.uploadCustomerAssignmentContract({
              customer: user.organizationId,
              body: {
                file: action.payload.file,
              },
            });

        return source$.pipe(
          tap((response) => {
            action.payload.callback(response.contractId);
          }),
          map(() => baseAssignmentsActions.uploadAssignmentContractSuccess()),
          catchError(() => of(baseAssignmentsActions.uploadAssignmentContractError()))
        );
      })
    )
  );

  updatePSPProfilePersonalId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseAssignmentsActions.updatePSPProfilePersonalId),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.assignmentsService
          .updatePSPProfilePersonalId({
            psp: user.organizationId,
            ...action.payload,
          })
          .pipe(
            mergeMap((_) => [
              baseAssignmentsActions.successUpdatePSPProfilePersonalId(),
              baseAssignmentsActions.loadAssignment({ payload: { assignment: action.payload.assignment } }),
            ]),
            catchError(() => of(baseAssignmentsActions.errorUpdatePSPProfilePersonalId()))
          );
      })
    )
  );

  /**
   * Posts the file, then describes it with the id the upload returned. Chaining both calls here
   * keeps them in the uploader's own request flow, which is what the backend's adoption check
   * requires, and spares the component from holding the pending metadata between two dispatches.
   */
  uploadPspAssignmentDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseAssignmentsActions.uploadPspAssignmentDocument),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        const { assignment, file, body } = action.payload;

        return this.assignmentsService
          .uploadPspAssignmentDocument({ psp: user.organizationId, assignment, body: { file } })
          .pipe(
            switchMap((response) =>
              this.assignmentsService.updatePspAssignmentDocument({
                psp: user.organizationId,
                assignment,
                document: response.documentId as string,
                body,
              })
            ),
            mergeMap(() => [
              baseAssignmentsActions.successUploadPspAssignmentDocument(),
              baseAssignmentsActions.loadAssignment({ payload: { assignment } }),
            ]),
            catchError(() => of(baseAssignmentsActions.errorUploadPspAssignmentDocument()))
          );
      })
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store<BaseCoreState>,
    private assignmentsService: BaseAssignmentsService,
    private assignmentsHelpersService: BaseAssignmentsHelpersService,
    private roleService: BaseRolesService,
    private translateService: TranslateService,
    private modalService: BaseModalService,
    private activatedRoute: ActivatedRoute,
    private fileDownloadService: BaseFileDownloadService
  ) {}
}
